import { ToastMethods } from "components/ToastNotification";
import i18n from "i18n";
import { action, observable } from "mobx";
import type { Asset } from "models/asset/Asset";
import type { Book } from "models/book/Book";
import { bookRepository } from "repositories";

interface BookInterface {
  booksInfo: Book[];
  bookInfo: Book;
  assetsInfo: Asset[];
  bookTitle: string;
  setBooksInfo: (booksInfo: Book[]) => void;
  setOneBookInfo: (bookInfo: Book) => void;
  setBookAssets: (assets: Asset[]) => void;
  setBookTitle: (title: string) => void;
  fetchAllBooks: () => void;
  fetchBookTitle: (bookId: string) => void;
  fetchOneBookAssets: (bookId: string) => void;
  fetchBook: (bookId: string) => void;
}

const initialState = {
  booksInfo: [],
  bookInfo: {
    id: "",
    title: "",
    author: "",
    chapters: [],
    metadata: { packageSiteName: "", productType: "" },
  },
  assetsInfo: [],
  bookTitle: "",
};

const stateSetters = {
  setBooksInfo: action((booksInfo: Book[]) => {
    store.booksInfo = booksInfo;
  }),

  setOneBookInfo: action((bookInfo: Book) => {
    store.bookInfo = bookInfo;
  }),

  setBookAssets: action((assets: Asset[]) => {
    store.assetsInfo = assets;
  }),
  setBookTitle: action((title: string) => {
    store.bookTitle = title;
  }),
};

const isExtraMaterialPackage = (metadata: { packageSiteName: string; productType: string }) =>
  metadata?.packageSiteName === "ho-hoo" || metadata?.productType === "hoo";

const apiRequests = {
  fetchAllBooks: action(() => {
    bookRepository
      .getAllBooks()
      .then((allBooks: Book[]) => {
        const books = allBooks.filter((license: Book) => isExtraMaterialPackage(license.metadata));
        store.setBooksInfo(books);
      })
      .catch(() => {
        ToastMethods.showToast(i18n.t("toast:book.error.fetchAllBooks"), "error");
      });
  }),

  fetchOneBookAssets: action((bookId: string) => {
    bookRepository
      .getOneBookAssets(bookId)
      .then((assets: Asset[]) => {
        store.setBookAssets(assets);
      })
      .catch(() => {
        ToastMethods.showToast(i18n.t("toast:book.error.fetchOneBookAssets"), "error");
      });
  }),

  fetchBook: action((bookId: string) => {
    bookRepository
      .getOneBook(bookId)
      .then((book: Book) => {
        store.setOneBookInfo(book);
      })
      .catch(() => {
        ToastMethods.showToast(i18n.t("toast:book.error.fetchOneBookInfo"), "error");
      });
  }),
  fetchBookTitle: action((bookId: string) => {
    bookRepository
      .fetchBookTitle(bookId)
      .then((book: { id: string; title: string }) => {
        store.setBookTitle(book.title);
      })
      .catch(() => {
        ToastMethods.showToast(i18n.t("toast:book.error.fetchBookTitleInfo"), "error");
      });
  }),
};

const store: BookInterface = observable({
  ...stateSetters,
  ...apiRequests,
  ...initialState,
} as BookInterface);

export const useBook = (): BookInterface => store;
